/**
 * AR Vision Demo — scripted prompt → tool/narration map + tool registry.
 *
 * THROWAWAY DEMO CODE. This is the deterministic spine: ~11 demo prompts each
 * map to (a) streamed narration text and (b) an ordered list of frontend tools
 * to invoke (by name). The chat-input interceptor (`installDemoInterceptor`,
 * wired in `DemoChatInterceptor`) matches the user's message against these
 * prompts; on a match it BYPASSES the real RPC call, streams the narration into
 * the existing chat UI, and fires the named tools that pages have registered
 * via `useArAgentPage`.
 *
 * Adding a beat (done by the fan-out tickets T2–T9): append a `ArDemoScript`
 * entry here whose `toolNames` reference the tools that beat's page registers.
 */

import type { FrontendToolDefinition } from '@/hooks/useFrontendTool';
// AR Email Intelligence Demo (E0 / GEN-2262) — additive beats kept in a separate
// array to minimize merge conflicts as E1–E4 fan out. Concatenated into the
// matched set in `matchArDemoScript` below (the only seam). Delete this import +
// the `MATCHABLE_SCRIPTS` concat to remove the email-intel demo.
import { AR_INTEL_SCRIPTS } from './email-intel/intel-script';

// ---------------------------------------------------------------------------
// Tool registry — pages register their built tools here via useArAgentPage so
// the interceptor can look one up by name regardless of which page mounted it.
// ---------------------------------------------------------------------------
const toolRegistry = new Map<string, FrontendToolDefinition>();

/** Register (replace) the tools owned by a page id. */
export function registerArDemoTools(
  pageId: string,
  tools: FrontendToolDefinition[],
): void {
  // Namespacing by pageId isn't required since tool names are unique, but we
  // keep the last registration per tool name to win (latest mount).
  for (const tool of tools) {
    toolRegistry.set(tool.name, tool);
  }
  // pageId is accepted for clarity / future namespacing; unused today.
  void pageId;
}

/** Look up a registered tool by name. */
export function getArDemoTool(
  name: string,
): FrontendToolDefinition | undefined {
  return toolRegistry.get(name);
}

// ---------------------------------------------------------------------------
// Scripted beats.
// ---------------------------------------------------------------------------
export interface ArDemoScript {
  /** Human label for the beat (storyboard scene). */
  scene: string;
  /**
   * Prompt matchers. A user message matches if, after lowercasing+trimming, it
   * equals one of these OR contains all words of one of these phrases. Keep
   * them distinctive so beats never collide.
   */
  prompts: string[];
  /**
   * Looser fallback matcher: synonym groups (AND-of-ORs). When no `prompts`
   * entry matches exactly, a message matches this beat if EVERY group has at
   * least one of its synonyms present as a whole word. Among beats that match,
   * the one with the most synonym hits wins. Lets off-script paraphrases
   * ("pull that invoice back up") still trigger the right scene without an LLM.
   * Keep groups distinctive so beats don't collide (e.g. scene 5 requires a
   * "weekly/remind" group that scene 9 lacks). Tokens are matched whole-word.
   */
  intent?: string[][];
  /** Narration streamed into the chat as Nami's reply. */
  narration: string;
  /**
   * Tool names to invoke in order once narration finishes. The interceptor
   * resolves each against the registry; missing tools are skipped with a warn
   * (so a not-yet-built fan-out beat degrades gracefully).
   */
  toolNames: string[];
  /**
   * Run the tools BEFORE the narration instead of after. For beats whose tool
   * plays an on-screen animation (e.g. the workflow building itself), so Nami's
   * reply only lands once it finishes. The tools must be awaitable.
   */
  narrateAfterTools?: boolean;
}

/**
 * The scripted beats. T1 ships the spine + placeholders; each fan-out ticket
 * fills in the `toolNames` for its page once its tools exist. Narration is
 * final-ish copy so the demo reads well even before every tool lands.
 */
export const AR_DEMO_SCRIPTS: ArDemoScript[] = [
  {
    scene: 'Scene 2 — show the just-sent invoice',
    // NB: no bare "show me the invoice" prompt — its words are all substrings of
    // the AP demo's "show me the last invoices from this supplier" (invoice ⊂
    // invoices), which would let this AR beat hijack that AP prompt. Bare
    // "show me the invoice" still matches via the intent fallback below.
    prompts: [
      'show me the invoice that was just sent',
      'show the invoice that was just sent',
    ],
    intent: [
      ['invoice', 'bill', 'inv'],
      ['sent', 'send', 'show', 'see', 'pull', 'open', 'view', 'just', 'bring'],
    ],
    narration:
      'Here’s invoice INV-48217, sent to Harbor Foods Co. the moment the order closed. You can see the line items, totals, and the email that went out on our branded letterhead.',
    toolNames: ['ar_navigate_invoice'],
  },
  {
    scene: 'Scene 3 — shipping terms for this customer',
    prompts: [
      'show me the shipping terms for this customer',
      'show the shipping terms',
      'shipping terms for this customer',
    ],
    intent: [
      ['shipping', 'ship', 'freight', 'delivery', 'fob'],
      ['terms', 'term', 'rule', 'rules', 'condition', 'conditions'],
    ],
    narration:
      'Harbor Foods is on FOB Origin, so we invoice the moment the carrier picks up. Here’s their collections profile with the shipping-terms rule that controls when invoices are sent.',
    toolNames: ['ar_navigate_customer_profile'],
  },
  {
    scene: 'Scene 4 — all current AR information',
    prompts: [
      'show me all the current information on ar',
      'show all the current information on ar',
      'show me all current ar information',
      'all current information on ar',
    ],
    intent: [
      [
        'all',
        'everything',
        'full',
        'overall',
        'overview',
        'current',
        'summary',
      ],
      [
        'ar',
        'receivable',
        'receivables',
        'information',
        'info',
        'picture',
        'status',
      ],
    ],
    narration:
      'Here’s the full AR picture: aging, open collections, disputes, collection threads, and the actions I’ve queued — emails, calls, and credit holds — all on one screen.',
    toolNames: ['ar_navigate_summary'],
  },
  {
    scene: 'Scene 5 — create weekly reminder workflow',
    prompts: [
      'create me a workflow that reminds customers to pay every week',
      'create a workflow that reminds customers to pay every week',
      'create a weekly payment reminder workflow',
    ],
    intent: [
      ['create', 'build', 'make', 'set', 'setup', 'new', 'automate'],
      ['workflow', 'automation', 'flow', 'process', 'job'],
      [
        'week',
        'weekly',
        'remind',
        'reminder',
        'reminders',
        'pay',
        'payment',
        'every',
      ],
    ],
    narration:
      'Done — here’s the weekly workflow I built. Every Monday at 8am it (1) triggers on schedule, (2) finds customers with unpaid invoices, (3) generates each one’s statement PDF, and (4) emails it to them. Click any step on the canvas to inspect it.',
    toolNames: ['ar_create_reminder_workflow'],
    narrateAfterTools: true,
  },
  {
    scene: 'Scene 5b — highlight + bold overdue lines',
    prompts: [
      'actually can we highlight the overdue lines in yellow and bold their text',
      'highlight the overdue lines in yellow and bold their text',
      'highlight the overdue lines in yellow and bold the text',
      'bold and highlight the overdue lines',
      'highlight overdue lines in yellow',
    ],
    intent: [
      ['highlight', 'bold', 'yellow', 'emphasize'],
      ['overdue', 'line', 'lines', 'invoice', 'invoices', 'text'],
    ],
    narration:
      'Done — the statement now bolds each overdue invoice and highlights it in yellow so it stands out.',
    toolNames: ['ar_highlight_statement'],
    narrateAfterTools: true,
  },
  {
    scene: 'Scene 6 — last 5 reminder emails',
    prompts: [
      'show me the last 5 email reminders that have been sent like this',
      'show the last 5 email reminders',
      'show me the last 5 reminders',
      'last 5 email reminders',
    ],
    intent: [
      ['last', 'recent', 'past', 'previous', 'history', 'sent'],
      // NB: no bare '5'/'five' — that let "show me the last 5 invoices" (an AP
      // demo prompt) match here. Require an explicit reminder/email word.
      ['reminder', 'reminders', 'email', 'emails'],
    ],
    narration:
      'Here are the last five reminder emails the automation sent. Each one includes the attached statement PDF showing the overdue invoices bolded and highlighted in yellow.',
    toolNames: ['ar_navigate_reminders'],
  },
  {
    scene: 'Scene 7 — high-priority actions',
    prompts: [
      'are there any high-priority actions we need to take in ar',
      'are there any high priority actions we need to take in ar',
      'high priority actions in ar',
      'any high-priority actions',
    ],
    intent: [
      ['high', 'urgent', 'important', 'priority', 'critical', 'pressing'],
      [
        'action',
        'actions',
        'task',
        'tasks',
        'todo',
        'attention',
        'handle',
        'address',
        'priority',
      ],
    ],
    narration:
      'Yes — Cascade Manufacturing is four weeks overdue on $128,400. I recommend calling them to warn of a credit hold before it escalates. Here’s their collections profile.',
    toolNames: ['ar_navigate_overdue_customer', 'ar_recommend_call'],
  },
  {
    scene: 'Scene 8 — Nami calls the customer',
    prompts: [
      'call the customer',
      'yes call them',
      'place the call',
      'nami calls the customer',
    ],
    intent: [
      ['call', 'phone', 'dial', 'ring', 'reach'],
      ['customer', 'them', 'cascade', 'client', 'account', 'they'],
    ],
    narration:
      'Placing the call now. You’ll see the live status and transcript on screen as I speak with their accounts team.',
    toolNames: ['ar_place_call'],
  },
  {
    scene: 'Scene 9 — amend the workflow',
    prompts: [
      'add that to the workflow',
      'add this to the workflow',
      'add it to the workflow',
    ],
    intent: [
      ['add', 'append', 'include', 'put', 'attach', 'incorporate'],
      ['workflow', 'automation', 'flow', 'process', 'job'],
    ],
    narration:
      'Added. The workflow now branches: if an invoice is overdue by more than a month, I place a call to open a dispute conversation instead of just emailing.',
    toolNames: ['ar_amend_workflow'],
    narrateAfterTools: true,
  },
  {
    scene: 'Scene 10 — payment disputes needing approval',
    prompts: [
      'show any payment disputes that need approval',
      'show payment disputes that need approval',
      'show me payment disputes that need approval',
      'payment disputes that need approval',
    ],
    intent: [
      [
        'dispute',
        'disputes',
        'disputed',
        'shortpay',
        'chargeback',
        'deduction',
        'deductions',
      ],
      ['approval', 'approve', 'payment', 'pending', 'need', 'needs', 'review'],
    ],
    narration:
      'Granite Peak short-paid invoice INV-48109 by 50%, but the bill of lading shows only 10% of the shipment was rejected. Here’s the collection thread with the evidence in view.',
    toolNames: ['ar_navigate_dispute_thread'],
  },
  {
    scene: 'Scene 11 — approve the rebuttal',
    prompts: [
      'approve and send',
      'approve and send it',
      'approve the rebuttal',
      'approve the email',
      'approve and send the rebuttal',
    ],
    intent: [
      ['approve', 'approved', 'accept', 'send', 'confirm', 'ok', 'okay', 'yes'],
      ['rebuttal', 'response', 'reply', 'email', 'draft', 'message', 'it'],
    ],
    narration:
      'Done — I’ve approved the rebuttal and sent it to Granite Peak, with the bill of lading attached showing only 10% of the shipment was rejected.',
    toolNames: ['ar_approve_rebuttal'],
    narrateAfterTools: true,
  },

  // =========================================================================
  // BULK STATEMENT SEND — the "approve 25 payment reminders" inbox task.
  // Three beats: open the real Send Statement dialog on the 25-customer batch,
  // send the whole batch at once, then tie back to the workflow that queued it.
  // Tools (`ar_open_bulk_statements`, `ar_send_all_statements`) are registered
  // by the AR summary page; the workflow tie-in reuses scene 5's canvas tool.
  // =========================================================================
  {
    scene: 'Bulk statements — open the batch to approve',
    prompts: [
      'bring up the payment reminders to approve',
      'show me the payment reminders to approve',
      'approve the 25 payment reminders',
      'approve the payment reminders',
      'open the bulk statements',
      'open the payment reminders',
    ],
    intent: [
      ['approve', 'review', 'open', 'bring', 'pull', 'show'],
      ['reminders', 'reminder', 'statements', 'statement'],
      ['payment', 'bulk', 'batch', '25', 'queued', 'outstanding'],
    ],
    narration:
      'Here are the 25 overdue customers queued for a payment reminder. Each one has its statement, recipients, and email drafted and ready — you can review or edit any of them, or send the whole batch at once.',
    toolNames: ['ar_open_bulk_statements'],
  },
  {
    scene: 'Bulk statements — send the whole batch',
    prompts: [
      'send them all',
      'send all the statements',
      'send all the reminders',
      'send all the payment reminders',
      'approve and send all',
      'send all 25',
    ],
    intent: [
      ['send', 'fire', 'dispatch', 'blast', 'submit'],
      ['all', 'every', 'everyone', 'them', '25', 'batch'],
    ],
    narration:
      'Done — all 25 statements are on their way. Each customer got their own statement of outstanding invoices on our branded letterhead. I’ve marked the task complete.',
    toolNames: ['ar_send_all_statements'],
    narrateAfterTools: true,
  },
  {
    scene: 'Bulk statements — show the workflow behind them',
    prompts: [
      'show the workflow that sends these',
      'show me the workflow behind this',
      'what workflow sends these statements',
      'show the reminder workflow',
    ],
    intent: [
      ['show', 'see', 'view', 'open', 'what', 'behind'],
      ['workflow', 'automation', 'flow', 'process'],
      ['sends', 'send', 'reminder', 'reminders', 'these', 'statements'],
    ],
    narration:
      'Here’s the workflow behind these reminders: every Monday it finds customers with unpaid invoices and emails each one their statement of outstanding invoices — the same batch you just approved.',
    toolNames: ['ar_create_reminder_workflow'],
    narrateAfterTools: true,
  },

  // =========================================================================
  // AR TECHNICAL DEMO (F0 / GEN-2256) — Crestline storyline beats.
  //
  // Three flows, 14 scenes + 3 per-flow ENTRY prompts (all independently
  // matchable for non-linear entry, storyboard §2b). Each beat fires its
  // scene-checkpoint tool FIRST (`ar_tech_checkpoint_*`, applies prerequisite
  // fixture state so the flow stands up cold) then a navigation tool
  // (`ar_tech_navigate_*`, jumps to the real AR surface F1-F4 wire). Both are
  // registered eagerly by `useTechnicalDemoTools`. Action tools the page UIs
  // own (evidence-pull animation, draft dialog, portal, contract card) are
  // appended by F1-F4 to these `toolNames` once built; missing tools degrade
  // gracefully (skipped with a warn).
  //
  // Intent groups are kept distinctive from the vision-demo beats above
  // (Crestline / remittance / deduction / reconcile vocabulary) so the two
  // storylines never collide under `?demo=ar`.
  // =========================================================================

  // ---- FLOW 1 entry + scenes 1-5 ----
  {
    scene: 'Flow 1 entry / Scene 2 — process the Crestline remittance',
    prompts: [
      'process the crestline remittance',
      'process this remittance',
      'process the remittance',
      'work the crestline remittance',
    ],
    intent: [
      ['process', 'work', 'match', 'apply', 'handle', 'open', 'review'],
      ['remittance', 'ra', 'crestline', 'advice'],
    ],
    narration:
      'Processing remittance RA-7741 from Crestline Markets — the remittance just lists invoices, balances and what they paid, with no explanation. INV-48811 ($18,500) auto-matches in full. INV-48810 came in $1,250 short with no reason cited at all. INV-48812 came in $3,100 short, citing a chargeback — claim #CR-DED-99214 — but no basis. Two different problems: one I can email about, one I’ll need their portal for. Here’s the remittance with the matching applied.',
    toolNames: [
      'ar_tech_checkpoint_applyRemittanceMatched',
      'ar_tech_navigate_remittance',
    ],
  },
  {
    scene: 'Scene 1 — Crestline remittance email arrives',
    prompts: [
      'show the crestline remittance email',
      'show the remittance email',
      'show me the remittance that arrived',
    ],
    intent: [
      ['remittance', 'advice', 'ra'],
      ['email', 'arrived', 'inbound', 'received', 'notification'],
    ],
    narration:
      'A remittance just arrived from Crestline Markets — RA-7741, stated $84,250, ACH. Here’s the inbound email with the remittance PDF attached.',
    // F1 (GEN-2257) appends its page-owned Scene-1 action tool. The eager nav
    // (`ar_tech_navigate_remittance`) opens the page; `ar_tech_show_remittance_email`
    // is the remittance-page-owned beat (registered via useArAgentPage) that
    // brings the inbound email + PDF into view. Missing tools degrade gracefully.
    toolNames: ['ar_tech_navigate_remittance', 'ar_tech_show_remittance_email'],
  },
  {
    scene: 'Scene 3 — short-pay opens collection thread COL-1042',
    prompts: [
      'open the short pay thread',
      'show the short pay thread',
      'show the short pay dispute for invoice 48810',
    ],
    intent: [
      ['shortpay', 'short', 'underpaid', 'withheld'],
      ['thread', 'dispute', 'collection', 'pay'],
    ],
    narration:
      'INV-48810 was short-paid $1,250 with no reason cited anywhere on the remittance. I’ve opened short-pay thread COL-1042 and scoped it to the invoice. Here’s the thread.',
    toolNames: [
      'ar_tech_checkpoint_applyShortPayThreadOpened',
      'ar_tech_navigate_short_pay_thread',
    ],
  },
  {
    scene: 'Scene 4 — evidence assembles on the short-pay thread',
    prompts: [
      'gather the evidence',
      'assemble the evidence',
      'pull the evidence for the short pay',
      'show me the evidence',
    ],
    intent: [
      ['gather', 'assemble', 'pull', 'collect', 'build', 'show'],
      ['evidence', 'documents', 'proof', 'bol', 'pod'],
    ],
    narration:
      'Evidence gathered: the original invoice on our letterhead, the signed BOL and POD from Sentinel Freight showing a full clean delivery, and two internal confirmations — warehouse confirms a complete pick/pack, CS confirms no claim or dispute on file. Each is traced back to the email it arrived on. Nothing on our side explains the $1,250 short — so we ask Crestline.',
    toolNames: [
      'ar_tech_checkpoint_applyShortPayThreadOpened',
      'ar_tech_navigate_short_pay_thread',
      // F2 (GEN-2258): play the evidence pull-in animation on the thread.
      'ar_tech_pull_evidence',
    ],
  },
  {
    scene: 'Scene 5 — draft the rebuttal',
    prompts: [
      'draft the rebuttal',
      'draft the rebuttal email',
      'write the rebuttal',
    ],
    intent: [
      ['draft', 'write', 'compose', 'prepare', 'rebuttal'],
      ['rebuttal', 'email', 'reply', 'response', 'dispute'],
    ],
    narration:
      'I’ve drafted an email to deductions@crestlinemarkets.com, CC’ing Dana Reyes (the rep on the order), with the POD, BOL and original invoice attached, asking Crestline to explain or remit the unexplained $1,250 short on INV-48810. Review it below — say “send the rebuttal” and I’ll send it.',
    toolNames: [
      'ar_tech_checkpoint_applyRebuttalReady',
      'ar_tech_navigate_short_pay_thread',
      // F2 (GEN-2258): surface + pulse the drafted rebuttal panel.
      'ar_tech_show_rebuttal',
    ],
  },
  {
    scene: 'Scene 5b — approve & send the rebuttal',
    // Distinct from the vision-demo "approve and send" beat (Granite Peak): the
    // prompts are short-pay/Crestline flavoured so they fast-path here, and the
    // intent uses send-only verbs (no "approve") so vision's approve/accept
    // paraphrases still resolve to that beat.
    prompts: [
      'send the rebuttal',
      'send the rebuttal email',
      'send the rebuttal to crestline',
      'send the crestline rebuttal',
    ],
    intent: [
      ['send', 'submit', 'dispatch', 'fire', 'transmit', 'deliver'],
      ['rebuttal', 'email', 'crestline'],
    ],
    narration:
      'Sent. The rebuttal is on its way to Crestline’s AP team, CC’ing Dana Reyes, with the POD, BOL and original invoice attached. I’ve logged it on the thread — the ball’s in their court to explain or remit the $1,250.',
    toolNames: [
      'ar_tech_checkpoint_applyRebuttalReady',
      'ar_tech_navigate_short_pay_thread',
      // F2 (GEN-2258): flip the draft to approved-and-sent + log the activity.
      'ar_tech_approve_rebuttal',
    ],
    narrateAfterTools: true,
  },

  // ---- FLOW 2 entry + scenes 6-11 ----
  {
    scene: 'Flow 2 entry / Scene 6 — work the Crestline deductions',
    prompts: [
      'work the crestline deductions',
      'work the deductions',
      'open the crestline deductions',
      'show the deductions',
    ],
    intent: [
      ['work', 'open', 'show', 'handle', 'review', 'process'],
      ['deduction', 'deductions', 'chargeback', 'chargebacks'],
    ],
    narration:
      'INV-48812 came in $3,100 short, citing chargeback #CR-DED-99214 — but the remittance gives no basis for it. Unlike the INV-48810 short, this one has a claim number, which means the supporting backup lives on Crestline’s vendor portal. I’ve opened deduction thread COL-1043; next step is to pull that backup. Here’s the thread.',
    toolNames: [
      'ar_tech_checkpoint_applyDeductionThreadOpened',
      'ar_tech_navigate_deduction_thread',
    ],
  },
  {
    scene: 'Scene 7 — offer to pull the portal backups',
    prompts: [
      'pull the backups from their portal',
      'get the backups from their portal',
      'pull the backups from the portal',
      'get the backups from the portal',
    ],
    intent: [
      ['pull', 'get', 'fetch', 'download', 'retrieve'],
      ['backup', 'backups', 'documentation', 'docs'],
      ['portal', 'crestline', 'vendor', 'their'],
    ],
    narration:
      'Chargeback #CR-DED-99214’s backup lives on Crestline’s vendor portal. I’m logging in now: opening the deductions list, selecting #CR-DED-99214, and downloading the backup PDFs. They’ll file straight onto COL-1043.',
    // AR Technical Demo (F3 / GEN-2259): stand up the deduction thread, then
    // have Nami OPERATE the portal — login → list → download — which fires the
    // F0 applyPortalBackupsFiled checkpoint and returns to the thread.
    toolNames: [
      'ar_tech_checkpoint_applyDeductionThreadOpened',
      'ar_tech_pull_portal_backups',
    ],
    narrateAfterTools: true,
  },
  {
    scene: 'Scene 8-9 — backups downloaded and filed to the thread',
    prompts: [
      'file the backups',
      'file the backups to the thread',
      'the backups are downloaded',
    ],
    intent: [
      ['file', 'filed', 'attach', 'add', 'land', 'downloaded'],
      ['backup', 'backups', 'documentation', 'docs'],
    ],
    narration:
      'I stepped through Crestline’s portal — logged in, found #CR-DED-99214, and downloaded the backup PDFs. They’re now filed as evidence on COL-1043. The backup is Crestline’s receiving report claiming a 10% shortage on the order.',
    toolNames: [
      'ar_tech_checkpoint_applyPortalBackupsFiled',
      'ar_tech_navigate_deduction_thread',
    ],
  },
  {
    scene: 'Scene 9b — summarize the backup files + recommend',
    prompts: [
      'what is in the backup files',
      'what do the backup files say',
      'summarize the backup documents',
      'read the backup documents',
      'what do you recommend on the chargeback',
    ],
    intent: [
      [
        'summarize',
        'read',
        'explain',
        'contents',
        'recommend',
        'recommendation',
      ],
      ['backup', 'backups', 'document', 'documents', 'file', 'files'],
    ],
    narration:
      'I read both files Crestline filed for #CR-DED-99214. Their receiving report logs 720 of 800 cases — a claimed 10% shortage on INV-48812 — and the shortage-claim form bills that back as $3,100. Neither carries a signed POD or BOL. Against that, Sentinel’s signed proof of delivery and bill of lading show all 800 cases delivered in full and signed for, clean, with no OS&D noted. Crestline’s paperwork is self-reported and contradicts the carrier’s signed record. My recommendation: dispute the full $3,100 as unsupported and file it on their portal with our POD and BOL attached.',
    toolNames: [
      'ar_tech_checkpoint_applyDeductionValidated',
      'ar_tech_navigate_deduction_thread',
      // F2 (GEN-2258): surface + pulse the chargeback-validation card.
      'ar_tech_validate_deduction',
    ],
  },
  {
    scene: 'Scene 10 — validate the deduction against the contract',
    prompts: [
      'check it against the contract',
      'check the deduction against the contract',
      'validate against the contract',
      'compare it to the contract',
    ],
    intent: [
      ['check', 'validate', 'compare', 'verify', 'against'],
      ['contract', 'terms', 'agreement', 'msa'],
    ],
    narration:
      'The portal backup claims a 10% shortage on INV-48812. But Sentinel’s signed POD and BOL show the order was delivered in full and signed for — clean, no exceptions. The backup doesn’t support the claim, so the full $3,100 chargeback is disputable.',
    toolNames: [
      'ar_tech_checkpoint_applyDeductionValidated',
      'ar_tech_navigate_deduction_thread',
      // F2 (GEN-2258): surface + pulse the chargeback-validation card.
      'ar_tech_validate_deduction',
    ],
  },
  {
    scene: 'Scene 11 — file the claim back on the portal',
    prompts: [
      'file the claim',
      'file the claim on their portal',
      'file the dispute claim',
      'dispute the chargeback',
    ],
    intent: [
      ['file', 'submit', 'dispute', 'lodge', 'raise'],
      ['claim', 'dispute', 'chargeback', 'deduction'],
    ],
    narration:
      'I’ve filed a claim on Crestline’s portal disputing the full $3,100 chargeback, with our signed POD and BOL attached. Confirmation #CR-CLAIM-44190 — logged on the thread. I’ll re-check the portal for Crestline’s response tomorrow and surface any update here.',
    // AR Technical Demo (F3 / GEN-2259): ensure the deduction is validated, then
    // have Nami OPEN the portal claim form, fill it (deduction ref, $3,100,
    // supporting docs) and submit — which fires the F0 applyClaimFiled
    // checkpoint and shows the confirmation #.
    toolNames: [
      'ar_tech_checkpoint_applyDeductionValidated',
      'ar_tech_file_portal_claim',
    ],
    narrateAfterTools: true,
  },

  // ---- FLOW 3 entry + scenes 12-14 ----
  {
    scene: "Flow 3 entry / Scene 12 — reconcile today's bank deposits",
    prompts: [
      'reconcile today s bank deposits',
      'reconcile the bank deposits',
      'reconcile todays deposits',
      'reconcile the deposits',
      'sync the bank transactions',
    ],
    intent: [
      ['reconcile', 'sync', 'match', 'pull'],
      ['bank', 'deposit', 'deposits', 'cash', 'transactions'],
    ],
    narration:
      'Pulling today’s incoming deposits. Crestline’s $70,150 ACH credit reconciles to remittance RA-7741 — covering INV-48810, INV-48811 and the short-paid INV-48812 — at 98% confidence. One combined deposit doesn’t cleanly match and is flagged for review.',
    toolNames: [
      'ar_tech_checkpoint_applyBankDepositsReconciled',
      'ar_tech_navigate_reconciliation',
    ],
  },
  {
    scene: 'Scene 13 — deposit matches the ingested remittance',
    prompts: [
      'show the crestline deposit match',
      'match the deposit to the remittance',
      'show how the deposit matches the remittance',
    ],
    intent: [
      ['match', 'matches', 'link', 'reconcile', 'tie'],
      ['deposit', 'remittance', 'ra', 'crestline'],
    ],
    narration:
      'Crestline’s $70,150 deposit matches remittance RA-7741 covering three invoices — including the short-paid INV-48812. The cash is reconciled to the remittance and the invoices automatically, closing the loop opened in the remittance flow.',
    toolNames: [
      'ar_tech_checkpoint_applyBankDepositsReconciled',
      'ar_tech_navigate_reconciliation',
    ],
  },
  {
    scene: 'Scene 14 — resolve the reconciliation exception',
    prompts: [
      'resolve the exception',
      'resolve the bank exception',
      'fix the exception deposit',
      'resolve the unmatched deposit',
    ],
    intent: [
      ['resolve', 'fix', 'split', 'clear', 'handle'],
      ['exception', 'unmatched', 'combined', 'review'],
    ],
    narration:
      'The flagged deposit is a combined payment spanning two customers, Ridgeway and Valeport. I’ve proposed the split — confirm and both halves reconcile to their invoices.',
    toolNames: [
      'ar_tech_checkpoint_applyExceptionResolved',
      'ar_tech_navigate_reconciliation',
    ],
  },

  // -------------------------------------------------------------------------
  // Reports exploration — using the Custom Aging Report with Nami.
  // -------------------------------------------------------------------------
  {
    scene: 'Reports 1 — overdue invoices, oldest first',
    prompts: [
      'show me all invoices that are overdue sorted by oldest first',
      'show me all overdue invoices sorted by oldest first',
      'show me all overdue invoices oldest first',
      'overdue invoices oldest first',
      'show overdue invoices sorted oldest first',
    ],
    intent: [
      ['overdue', 'aging', 'past', 'late', 'unpaid'],
      ['invoice', 'invoices', 'bill', 'bills'],
      ['oldest', 'old', 'sorted', 'sort', 'age', 'aged'],
    ],
    narration:
      'Here’s every overdue invoice in the Custom Aging Report, oldest first — $325,700 across 9 invoices. Cedar Ridge Foods is 78 days out at the top, with Cascade and Granite Peak right behind it.',
    toolNames: ['ar_report_overdue_oldest_first'],
  },
  {
    scene: 'Reports 2 — status on these',
    prompts: [
      'what is the status on these',
      'whats the status on these',
      'what is the status of these',
      'status on these',
      'what is going on with these',
    ],
    intent: [
      ['status', 'standing', 'state', 'going', 'happening'],
      ['these', 'them', 'those', 'they'],
    ],
    // Narration-only — Nami reads the picture already on screen.
    narration:
      'Cedar Ridge Foods has the deepest exposure: $114,250 across four invoices, all overdue and getting larger. Cascade is 28 days out on $128,400 with weekly statements already going out, and Granite Peak’s $43,250 is the open short-pay dispute. The rest are smaller and newer to the overdue list.',
    toolNames: [],
  },
  {
    scene: 'Reports 3 — patterns with this customer',
    prompts: [
      'are there any patterns with this customer',
      'any patterns with this customer',
      'patterns with this customer',
      'do you see any patterns with this customer',
      'is there a pattern with this customer',
    ],
    intent: [
      ['pattern', 'patterns', 'trend', 'trends', 'behavior', 'behaviour'],
      [
        'customer',
        'account',
        'client',
        'cedar',
        'ridge',
        'this',
        'them',
        'they',
      ],
    ],
    narration:
      'Yes — Cedar Ridge Foods is trending the wrong way. Their average days-to-pay has jumped from about 26 to 58, on-time rate is down to 33%, and each new invoice is both larger and later. It lines up with a new AP contact, Brent Halloway, who took over in April. I’ve drafted a heads-up to Renee, their sales rep, recommending we slow new orders until they’re current — want me to send it?',
    toolNames: ['ar_report_drill_customer'],
  },
  {
    scene: 'Reports 3b — send the heads-up to the sales rep',
    prompts: [
      'send it to the rep',
      'send the email to the rep',
      'yes send it',
      'send the heads up',
      'send the note to sales',
    ],
    intent: [
      ['send', 'draft', 'approve', 'yes', 'go'],
      ['rep', 'sales', 'salesperson', 'renee', 'heads', 'note', 'email'],
    ],
    narration:
      'Sent — Renee has the heads-up with the payment-trend summary and the recommendation to pause new orders to Cedar Ridge until the past-due balance clears. I’ll keep working collections and update her when it does.',
    toolNames: ['ar_send_sales_rep_email'],
  },
];

/** Normalise a message for matching. */
function normalize(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Score a beat's `intent` groups against the message tokens. Returns the total
 * number of synonym hits when EVERY group is satisfied (at least one synonym
 * present as a whole word), or null when any group is missing — i.e. the beat
 * doesn't match. Whole-word (token-set) matching avoids false hits like "all"
 * inside "call".
 */
function scoreIntent(tokens: Set<string>, groups: string[][]): number | null {
  let total = 0;
  for (const group of groups) {
    const hits = group.filter((syn) => tokens.has(syn)).length;
    if (hits === 0) return null;
    total += hits;
  }
  return total;
}

/**
 * Find the scripted beat for a user message, or null if none matches.
 *
 * Two-tier match. (1) Precise fast-path: the normalised message equals a
 * prompt, or contains every word of one — rehearsed phrasings stay 100%
 * deterministic and return first. (2) Looser intent fallback: off-script
 * paraphrases match the best-scoring beat whose every `intent` group has a
 * whole-word hit (most synonym hits wins; ties resolve to declaration order).
 */
export function matchArDemoScript(message: string): ArDemoScript | null {
  const norm = normalize(message);
  if (!norm) return null;

  // AR Email Intelligence Demo (E0 / GEN-2262) — the email-intel beats are
  // matched through the SAME machinery; just concatenate them onto the Vision
  // beats here. Tool names are namespaced (`intel_*`) so the sets never collide.
  const MATCHABLE_SCRIPTS = [...AR_DEMO_SCRIPTS, ...AR_INTEL_SCRIPTS];

  // (1) Precise fast-path — exact equality or full word-containment.
  for (const script of MATCHABLE_SCRIPTS) {
    for (const prompt of script.prompts) {
      const np = normalize(prompt);
      if (norm === np) return script;
      const words = np.split(' ');
      if (words.length > 1 && words.every((w) => norm.includes(w))) {
        return script;
      }
    }
  }

  // (2) Intent fallback — best-scoring beat whose every group is satisfied.
  const tokens = new Set(norm.split(' '));
  let best: ArDemoScript | null = null;
  let bestScore = 0;
  for (const script of MATCHABLE_SCRIPTS) {
    if (!script.intent) continue;
    const score = scoreIntent(tokens, script.intent);
    if (score !== null && score > bestScore) {
      best = script;
      bestScore = score;
    }
  }
  return best;
}
